import React, { useState, useEffect } from "react";
import { Card, SH, fmtM } from "../shared/constants";

// ── Role badge ────────────────────────────────────────────────────────────────
const ROLE_LABELS = {
  seg:      { label: "RevXcel (SEG)", color: "#CC1A1A",  bg: "rgba(204,26,26,0.08)" },
  admin:    { label: "Admin",         color: "#7C3AED",  bg: "rgba(124,58,237,0.08)" },
  core:     { label: "Core User",     color: "#D97706",  bg: "rgba(217,119,6,0.08)"  },
  readonly: { label: "Read Only",     color: "#555555",  bg: "rgba(0,0,0,0.06)"      },
};
const ROLE_DESCRIPTIONS = {
  admin:    "Full access — Setup, Admin, Coaching, Billing",
  core:     "Can view all screens, generate AI reviews, add notes",
  readonly: "View-only — no editing, no AI features",
};
const RoleBadge = ({ role }) => {
  const r = ROLE_LABELS[role] || ROLE_LABELS.readonly;
  return <span style={{ fontSize:10, fontWeight:700, color:r.color, background:r.bg, borderRadius:5, padding:"2px 8px" }}>{r.label}</span>;
};

// ── Billing constants ─────────────────────────────────────────────────────────
const FLAT_FEE     = 199.00;
const MARKUP       = 2.0;
const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtCost      = (n) => n == null ? "—" : `$${(+n).toFixed(2)}`;
const fmtTokens    = (n) => n >= 1_000_000 ? `${(n/1_000_000).toFixed(2)}M` : n >= 1_000 ? `${(n/1_000).toFixed(1)}K` : String(n||0);
const FEATURE_LABELS = {
  coaching_1on1:     "Coaching · 1-on-1",
  coaching_pipeline: "Coaching · Pipeline",
  coaching_forecast: "Coaching · Forecast",
  coaching_activity: "Coaching · Activity",
  unknown:           "Other",
};

// ── Tab button ────────────────────────────────────────────────────────────────
const Tab = ({ id, label, icon, active, onClick }) => (
  <button onClick={() => onClick(id)}
    style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 16px", borderRadius:8,
      border:"none", cursor:"pointer", background:active?"rgba(204,26,26,0.08)":"transparent",
      borderLeft:active?"2px solid #CC1A1A":"2px solid transparent",
      width:"100%", textAlign:"left", marginBottom:4 }}>
    <span style={{ fontSize:16 }}>{icon}</span>
    <span style={{ fontSize:12, fontWeight:active?700:500, color:active?"#CC1A1A":"#555" }}>{label}</span>
  </button>
);


// ═══════════════════════════════════════════════════════════════════════════════
// Team Tab
// ═══════════════════════════════════════════════════════════════════════════════
const TeamTab = ({ user, orgId }) => {
  const [members,    setMembers]    = useState([]);
  const [pending,    setPending]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [email,      setEmail]      = useState("");
  const [role,       setRole]       = useState("core");
  const [inviting,   setInviting]   = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [error,      setError]      = useState(null);
  const [copied,     setCopied]     = useState(false);

  const loadMembers = () => {
    if (!orgId) return;
    setLoading(true);
    fetch(`/api/invite?action=list_members&org_id=${orgId}`)
      .then(r => r.json())
      .then(d => { setMembers(d.members||[]); setPending(d.pending||[]); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadMembers(); }, [orgId]);

  const sendInvite = async () => {
    if (!email.trim()) return setError("Email required");
    if (!email.includes("@")) return setError("Enter a valid email");
    setInviting(true); setError(null); setInviteLink(null);
    const res = await fetch("/api/invite?action=create", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ org_id:orgId, email:email.trim(), role, invited_by:user.id }),
    });
    const data = await res.json();
    setInviting(false);
    if (!res.ok) return setError(data.error || "Failed to create invite");
    setInviteLink(data.accept_url);
    setEmail("");
    loadMembers();
  };

  const copyLink = (link) => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const removeMember = async (memberId) => {
    if (!window.confirm("Remove this member?")) return;
    await fetch("/api/invite?action=remove_member", {
      method:"DELETE", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ member_id: memberId }),
    });
    loadMembers();
  };

  const inp = { flex:1, padding:"10px 14px", borderRadius:8, border:"1px solid rgba(0,0,0,0.10)",
    background:"#F8F8F8", fontSize:13, color:"#111", outline:"none", fontFamily:"'DM Sans',sans-serif" };

  return (
    <div>
      <SH title="Team & Access" sub="Manage who has access to this RevXcel workspace"/>

      <Card style={{marginBottom:20}}>
        <div style={{fontSize:12, fontWeight:700, color:"#111", marginBottom:14}}>Invite a team member</div>
        <div style={{display:"flex", gap:10, marginBottom:12, flexWrap:"wrap"}}>
          <input style={inp} type="email" placeholder="colleague@company.com" value={email}
            onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key==="Enter" && sendInvite()}/>
          <select value={role} onChange={e => setRole(e.target.value)}
            style={{padding:"10px 14px", borderRadius:8, border:"1px solid rgba(0,0,0,0.10)",
              background:"#F8F8F8", fontSize:13, color:"#111", cursor:"pointer", outline:"none"}}>
            <option value="admin">Admin</option>
            <option value="core">Core User</option>
            <option value="readonly">Read Only</option>
          </select>
          <button onClick={sendInvite} disabled={inviting}
            style={{padding:"10px 20px", background:inviting?"#aaa":"#CC1A1A", border:"none", borderRadius:8,
              color:"#fff", fontSize:13, fontWeight:700, cursor:inviting?"not-allowed":"pointer"}}>
            {inviting ? "Sending…" : "Send Invite"}
          </button>
        </div>

        <div style={{display:"flex", gap:10, flexWrap:"wrap"}}>
          {Object.entries(ROLE_DESCRIPTIONS).map(([r, desc]) => (
            <div key={r} onClick={() => setRole(r)}
              style={{flex:1, minWidth:160, padding:"10px 12px", borderRadius:8, cursor:"pointer",
                border:`1px solid ${role===r ? ROLE_LABELS[r].color+"40" : "rgba(0,0,0,0.07)"}`,
                background:role===r ? ROLE_LABELS[r].bg : "#FAFAFA"}}>
              <RoleBadge role={r}/>
              <div style={{fontSize:11, color:"#666", marginTop:5, lineHeight:1.5}}>{desc}</div>
            </div>
          ))}
        </div>

        {error && <div style={{marginTop:10, padding:"8px 12px", background:"rgba(220,38,38,0.06)", border:"1px solid rgba(220,38,38,0.15)", borderRadius:7, fontSize:12, color:"#DC2626"}}>{error}</div>}

        {inviteLink && (
          <div style={{marginTop:12, padding:"12px 14px", background:"rgba(22,163,74,0.06)", border:"1px solid rgba(22,163,74,0.15)", borderRadius:8}}>
            <div style={{fontSize:11, fontWeight:700, color:"#16A34A", marginBottom:6}}>✓ Invite created — copy and send this link</div>
            <div style={{display:"flex", gap:8, alignItems:"center"}}>
              <input readOnly value={inviteLink} style={{...inp, flex:1, fontSize:11, color:"#555", background:"#fff"}}/>
              <button onClick={() => copyLink(inviteLink)}
                style={{padding:"8px 14px", background:copied?"#16A34A":"rgba(22,163,74,0.10)",
                  border:"1px solid rgba(22,163,74,0.2)", borderRadius:7,
                  color:copied?"#fff":"#16A34A", fontSize:12, fontWeight:700, cursor:"pointer"}}>
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <div style={{fontSize:10, color:"#aaa", marginTop:6}}>Link expires in 7 days.</div>
          </div>
        )}
      </Card>

      <Card style={{marginBottom:20}}>
        <div style={{fontSize:12, fontWeight:700, color:"#111", marginBottom:14}}>
          Current members {members.length > 0 && <span style={{fontSize:10, color:"#aaa", fontWeight:400}}>({members.length})</span>}
        </div>
        {loading ? (
          <div style={{textAlign:"center", padding:30, color:"#aaa", fontSize:12}}>Loading…</div>
        ) : members.length === 0 ? (
          <div style={{textAlign:"center", padding:30, color:"#aaa", fontSize:12}}>No members yet</div>
        ) : (
          <div>
            <div style={{display:"grid", gridTemplateColumns:"1fr 120px 120px 40px", padding:"6px 12px", background:"#F8F8F8", borderRadius:7, marginBottom:4}}>
              {["Email","Role","Joined",""].map((h,i) => <div key={i} style={{fontSize:9, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:"0.06em"}}>{h}</div>)}
            </div>
            {members.map((m, i) => (
              <div key={m.id} style={{display:"grid", gridTemplateColumns:"1fr 120px 120px 40px", padding:"10px 12px",
                borderBottom:i<members.length-1?"1px solid rgba(0,0,0,0.04)":"none", alignItems:"center"}}>
                <div style={{fontSize:12, color:"#111"}}>{m.email}{m.user_id===user?.id&&<span style={{marginLeft:6, fontSize:9, color:"#aaa"}}>you</span>}</div>
                <div><RoleBadge role={m.role}/></div>
                <div style={{fontSize:11, color:"#aaa"}}>{new Date(m.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</div>
                <div>
                  {m.user_id!==user?.id && m.role!=="seg" && (
                    <button onClick={() => removeMember(m.id)}
                      style={{background:"none", border:"none", cursor:"pointer", fontSize:16, color:"#ddd", lineHeight:1}}
                      onMouseEnter={e => e.target.style.color="#DC2626"}
                      onMouseLeave={e => e.target.style.color="#ddd"}>×</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {pending.length > 0 && (
        <Card>
          <div style={{fontSize:12, fontWeight:700, color:"#111", marginBottom:14}}>
            Pending invites <span style={{fontSize:10, color:"#aaa", fontWeight:400}}>({pending.length})</span>
          </div>
          <div style={{display:"grid", gridTemplateColumns:"1fr 120px 140px", padding:"6px 12px", background:"#F8F8F8", borderRadius:7, marginBottom:4}}>
            {["Email","Role","Expires"].map((h,i) => <div key={i} style={{fontSize:9, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:"0.06em"}}>{h}</div>)}
          </div>
          {pending.map((p, i) => (
            <div key={p.id} style={{display:"grid", gridTemplateColumns:"1fr 120px 140px", padding:"10px 12px",
              borderBottom:i<pending.length-1?"1px solid rgba(0,0,0,0.04)":"none", alignItems:"center"}}>
              <div style={{fontSize:12, color:"#555"}}>{p.email}</div>
              <div><RoleBadge role={p.role}/></div>
              <div style={{fontSize:11, color:new Date(p.expires_at)<new Date()?"#DC2626":"#aaa"}}>
                {new Date(p.expires_at)<new Date()?"Expired":new Date(p.expires_at).toLocaleDateString("en-US",{month:"short",day:"numeric"})}
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Billing Tab
// ═══════════════════════════════════════════════════════════════════════════════
const BillingTab = ({ userId }) => {
  const [orgs,      setOrgs]      = useState([]);
  const [usage,     setUsage]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [month,     setMonth]     = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
  });
  const [copiedOrg, setCopiedOrg] = useState(null);
  const [expandOrg, setExpandOrg] = useState(null);

  useEffect(() => { load(); }, [month]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [orgsRes, usageRes] = await Promise.all([
        fetch("/api/admin?action=list_orgs"),
        fetch(`/api/admin?action=usage&month=${month}`),
      ]);
      if (!orgsRes.ok || !usageRes.ok) throw new Error("Failed to load billing data");
      setOrgs((await orgsRes.json()).orgs || []);
      setUsage((await usageRes.json()).usage || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const usageByOrg = {};
  usage.forEach(row => {
    const key = row.org_id || row.portal_id || "unassigned";
    if (!usageByOrg[key]) usageByOrg[key] = { rows:[], totalCost:0, totalInput:0, totalOutput:0, calls:0 };
    usageByOrg[key].rows.push(row);
    usageByOrg[key].totalCost   += +(row.cost_usd||0);
    usageByOrg[key].totalInput  += +(row.input_tokens||0);
    usageByOrg[key].totalOutput += +(row.output_tokens||0);
    usageByOrg[key].calls++;
  });

  const featureBreakdown = (orgKey) => {
    const byFeature = {};
    (usageByOrg[orgKey]?.rows||[]).forEach(r => {
      const f = r.feature||"unknown";
      if (!byFeature[f]) byFeature[f] = { calls:0, cost:0 };
      byFeature[f].calls++; byFeature[f].cost += +(r.cost_usd||0);
    });
    return Object.entries(byFeature).sort((a,b) => b[1].cost - a[1].cost);
  };

  const copyInvoice = (org, claudeCost) => {
    const total = FLAT_FEE + claudeCost;
    const [yr, mo] = month.split("-");
    const text = [
      `RevXcel Invoice — ${org.name}`,
      `Period: ${MONTH_LABELS[+mo-1]} ${yr}`,
      ``,
      `Platform fee:     $${FLAT_FEE.toFixed(2)}`,
      `AI usage:         $${claudeCost.toFixed(2)}`,
      `─────────────────────────────`,
      `Total due:        $${total.toFixed(2)}`,
      ``,
      `AI usage details:`,
      ...featureBreakdown(org.id).map(([f,d]) =>
        `  ${(FEATURE_LABELS[f]||f).padEnd(28)} ${d.calls} calls  $${d.cost.toFixed(4)}`
      ),
    ].join("\n");
    navigator.clipboard.writeText(text);
    setCopiedOrg(org.id);
    setTimeout(() => setCopiedOrg(null), 2500);
  };

  const monthOptions = Array.from({length:6}, (_,i) => {
    const d = new Date(); d.setMonth(d.getMonth()-i);
    const val = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    return { val, label:`${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}` };
  });

  const totalRevenue = orgs.length * FLAT_FEE + usage.reduce((a,r) => a+(+(r.cost_usd||0)*MARKUP), 0);

  return (
    <div>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12}}>
        <SH title="Billing & Usage" sub={`$${FLAT_FEE}/mo flat fee + AI usage billing`}/>
        <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
          {monthOptions.map(m => (
            <button key={m.val} onClick={() => setMonth(m.val)}
              style={{padding:"6px 12px", borderRadius:7,
                border:`1px solid ${month===m.val?"#CC1A1A":"rgba(0,0,0,0.10)"}`,
                background:month===m.val?"rgba(204,26,26,0.08)":"transparent",
                color:month===m.val?"#CC1A1A":"#555", fontSize:11, fontWeight:month===m.val?700:400, cursor:"pointer"}}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20}}>
        {[
          {l:"Active Orgs",    v:orgs.length,                                              s:"paying clients",    c:"#111"},
          {l:"Flat Fees",      v:`$${(orgs.length*FLAT_FEE).toFixed(2)}`,                 s:`${orgs.length} × $${FLAT_FEE}/mo`, c:"#CC1A1A"},
          {l:"AI Usage",   v:fmtCost(usage.reduce((a,r)=>a+(+(r.cost_usd||0)),0)),    s:`${usage.length} API calls`, c:"#7C3AED"},
          {l:"Total MRR Est.", v:`$${totalRevenue.toFixed(2)}`,                            s:"this month",        c:"#16A34A"},
        ].map((m,i) => (
          <div key={i} style={{background:"#F8F8F8", border:"1px solid rgba(0,0,0,0.07)", borderRadius:12, padding:"16px 18px"}}>
            <div style={{fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8}}>{m.l}</div>
            <div style={{fontSize:22, fontWeight:800, color:m.c, fontFamily:"'DM Mono',monospace", marginBottom:4}}>{m.v}</div>
            <div style={{fontSize:11, color:"#aaa"}}>{m.s}</div>
          </div>
        ))}
      </div>

      {loading && <div style={{textAlign:"center", padding:40, color:"#aaa", fontSize:13}}>Loading billing data…</div>}
      {error   && <div style={{padding:"12px 16px", background:"rgba(220,38,38,0.06)", border:"1px solid rgba(220,38,38,0.15)", borderRadius:8, fontSize:12, color:"#DC2626", marginBottom:16}}>{error}</div>}
      {!loading && orgs.length === 0 && <Card><div style={{textAlign:"center", padding:"30px 0", color:"#aaa", fontSize:13}}>No orgs found.</div></Card>}

      {orgs.map(org => {
        const ou = usageByOrg[org.id] || { totalCost:0, calls:0, totalInput:0, totalOutput:0 };
        const claudeCost = ou.totalCost * MARKUP;
        const total = FLAT_FEE + claudeCost;
        const isExp = expandOrg === org.id;

        return (
          <Card key={org.id} style={{marginBottom:12}}>
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12}}>
              <div>
                <div style={{fontSize:14, fontWeight:800, color:"#111"}}>{org.name}</div>
                <div style={{fontSize:10, color:"#aaa", marginTop:2, fontFamily:"'DM Mono',monospace"}}>{org.id}</div>
              </div>
              <div style={{display:"flex", gap:16, alignItems:"center", flexWrap:"wrap"}}>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:10, color:"#aaa", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em"}}>Platform</div>
                  <div style={{fontSize:16, fontWeight:700, color:"#111", fontFamily:"'DM Mono',monospace"}}>${FLAT_FEE.toFixed(2)}</div>
                </div>
                <div style={{color:"#ddd", fontSize:18}}>+</div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:10, color:"#aaa", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em"}}>Claude AI</div>
                  <div style={{fontSize:16, fontWeight:700, color:"#7C3AED", fontFamily:"'DM Mono',monospace"}}>{fmtCost(claudeCost)}</div>
                  <div style={{fontSize:9, color:"#aaa"}}>{ou.calls} calls</div>
                </div>
                <div style={{color:"#ddd", fontSize:18}}>=</div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:10, color:"#aaa", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em"}}>Total Due</div>
                  <div style={{fontSize:20, fontWeight:900, color:"#CC1A1A", fontFamily:"'DM Mono',monospace"}}>${total.toFixed(2)}</div>
                </div>
                <div style={{display:"flex", gap:8}}>
                  <button onClick={() => setExpandOrg(isExp?null:org.id)}
                    style={{padding:"8px 14px", borderRadius:7, border:"1px solid rgba(0,0,0,0.10)",
                      background:"transparent", fontSize:11, color:"#555", cursor:"pointer", fontWeight:600}}>
                    {isExp ? "Hide" : "Details"}
                  </button>
                  <button onClick={() => copyInvoice(org, claudeCost)}
                    style={{padding:"8px 16px", borderRadius:7, border:"none",
                      background:copiedOrg===org.id?"#16A34A":"#CC1A1A",
                      color:"#fff", fontSize:11, fontWeight:700, cursor:"pointer"}}>
                    {copiedOrg===org.id ? "✓ Copied!" : "Copy Invoice"}
                  </button>
                </div>
              </div>
            </div>

            {isExp && (
              <div style={{marginTop:16, paddingTop:16, borderTop:"1px solid rgba(0,0,0,0.06)"}}>
                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:16}}>
                  <div>
                    <div style={{fontSize:11, fontWeight:700, color:"#555", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:10}}>Usage by Feature</div>
                    {featureBreakdown(org.id).length === 0
                      ? <div style={{fontSize:12, color:"#aaa"}}>No AI usage this month</div>
                      : featureBreakdown(org.id).map(([f,d]) => (
                        <div key={f} style={{display:"flex", justifyContent:"space-between", alignItems:"center",
                          padding:"8px 10px", background:"#F8F8F8", borderRadius:7, marginBottom:4}}>
                          <div>
                            <div style={{fontSize:12, fontWeight:600, color:"#111"}}>{FEATURE_LABELS[f]||f}</div>
                            <div style={{fontSize:10, color:"#aaa"}}>{d.calls} call{d.calls!==1?"s":""}</div>
                          </div>
                          <div style={{fontSize:13, fontWeight:700, color:"#7C3AED", fontFamily:"'DM Mono',monospace"}}>{fmtCost(d.cost*MARKUP)}</div>
                        </div>
                      ))
                    }
                  </div>
                  <div>
                    <div style={{fontSize:11, fontWeight:700, color:"#555", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:10}}>Token Usage</div>
                    {[
                      {l:"Input tokens",  v:fmtTokens(ou.totalInput),                s:`$${((ou.totalInput/1_000_000)*3).toFixed(4)}`},
                      {l:"Output tokens", v:fmtTokens(ou.totalOutput),               s:`$${((ou.totalOutput/1_000_000)*15).toFixed(4)}`},
                      {l:"Total tokens",  v:fmtTokens(ou.totalInput+ou.totalOutput), s:"combined"},
                    ].map((t,i) => (
                      <div key={i} style={{display:"flex", justifyContent:"space-between", alignItems:"center",
                        padding:"8px 10px", background:"#F8F8F8", borderRadius:7, marginBottom:4}}>
                        <div style={{fontSize:12, color:"#555"}}>{t.l}</div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:13, fontWeight:700, color:"#111", fontFamily:"'DM Mono',monospace"}}>{t.v}</div>
                          <div style={{fontSize:9, color:"#aaa"}}>{t.s}</div>
                        </div>
                      </div>
                    ))}
                    <div style={{marginTop:12, padding:"10px 12px", background:"rgba(204,26,26,0.04)", border:"1px solid rgba(204,26,26,0.10)", borderRadius:8}}>
                      <div style={{fontSize:10, fontWeight:700, color:"#CC1A1A", marginBottom:6}}>Invoice Preview</div>
                      <pre style={{fontSize:10, color:"#555", margin:0, lineHeight:1.7, fontFamily:"'DM Mono',monospace", whiteSpace:"pre-wrap"}}>
{`Platform fee:    $${FLAT_FEE.toFixed(2)}
AI usage:        $${claudeCost.toFixed(2)}
─────────────────────
Total due:       $${total.toFixed(2)}`}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Main AdminScreen — Team + Billing only
// ═══════════════════════════════════════════════════════════════════════════════
export default function AdminScreen({ user, userId, settings, onSave, orgId }) {
  const [tab, setTab] = useState("team");

  const TABS = [
    { id:"team",    label:"Team",    icon:"👥" },
    { id:"billing", label:"Billing", icon:"💳" },
  ];

  return (
    <div className="fade-up" style={{display:"grid", gridTemplateColumns:"200px 1fr", gap:16}}>
      <div>
        <div style={{fontSize:11, fontWeight:700, color:"#555", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10}}>Admin</div>
        {TABS.map(t => <Tab key={t.id} {...t} active={tab===t.id} onClick={setTab}/>)}
      </div>
      <Card>
        {tab === "team"    && <TeamTab    user={user} orgId={orgId||"00000000-0000-0000-0000-000000000001"}/>}
        {tab === "billing" && <BillingTab userId={userId}/>}
      </Card>
    </div>
  );
}
